import { useState, useDeferredValue } from "react"
import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Search, Plus, Edit, Trash2, Layers, ChevronLeft, ChevronRight, Loader2, AlertCircle } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useApiQuery, useApiMutation } from "@/api/ApiQueryServiceProvider"
import { useToast } from "@/contexts/ToastContext"

// Types
export type NetworkType = "LoRa" | "WiFi" | "BLE" | "Zigbee"

export interface DeviceProfile {
  deviceProfileNumber: number
  deviceProfileName: string
  deviceProfileNetwork: NetworkType
  // Some API responses nest LoRa attributes under these objects
  deviceProfileLoraAttributes?: {
    region?: string
    macVersion?: string
    deviceSupportsOtaa?: boolean
    devicesupportsClassB?: boolean
  }
  deviceProfileAttributes?: {
    region?: string
    macVersion?: string
    deviceSupportsOtaa?: boolean
    devicesupportsClassB?: boolean
  }
  region?: string
  macVersion?: string
  supportsOtaa?: boolean
  supportsClassB?: boolean
}

const networkConfig: Record<NetworkType, { color: string }> = {
  LoRa: { color: "bg-trilogy-blue/15 text-trilogy-blue" },
  WiFi: { color: "bg-trilogy-navy/15 text-trilogy-navy" },
  BLE: { color: "bg-trilogy-yellow/15 text-trilogy-yellow" },
  Zigbee: { color: "bg-trilogy-red/15 text-trilogy-red" },
}

interface DeviceProfileListProps {
  onAdd?: () => void
  onEdit?: (profile: DeviceProfile) => void
}

export function DeviceProfileList({ onAdd, onEdit }: DeviceProfileListProps) {
  const { toast } = useToast()
  const [searchQuery, setSearchQuery] = useState("")
  const deferredSearchQuery = useDeferredValue(searchQuery)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedProfile, setSelectedProfile] = useState<DeviceProfile | null>(null)

  const deleteProfileMutation = useApiMutation('archiveDeviceProfile', 'DELETE', {
    invalidates: ['getDeviceProfiles'],
    onSuccess: () => {
      toast({ title: 'Profile Deleted', description: `Device profile "${selectedProfile?.deviceProfileName}" has been deleted.` })
      setIsDeleteDialogOpen(false)
      setSelectedProfile(null)
    },
    onError: (error) => {
      toast({ title: 'Deletion Failed', description: `Failed to delete profile: ${error.message}`, type: 'error' })
    },
  })

  const handleDelete = (profile: DeviceProfile) => {
    setSelectedProfile(profile)
    setIsDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (selectedProfile) {
      deleteProfileMutation.mutate({ deviceProfileNumber: selectedProfile.deviceProfileNumber })
    }
  }

  // Fetch profiles
  const {
    data: profilesResponse,
    isLoading,
    error,
  } = useApiQuery<any>("getDeviceProfiles")

  // Transform API response to flatten nested attributes
  const profiles: DeviceProfile[] = (Array.isArray(profilesResponse)
    ? profilesResponse
    : profilesResponse?.content?.deviceProfiles || []).map((profile: any) => {
      // Get network-specific attributes based on deviceProfileNetwork
      const networkType = profile.deviceProfileNetwork
      let networkAttributes: any = {}

      if (networkType === 'LoRa') {
        networkAttributes = profile.deviceProfileLoraAttributes || {}
      } else if (networkType === 'WiFi') {
        networkAttributes = profile.deviceProfileWifiAttributes || {}
      } else if (networkType === 'BLE') {
        networkAttributes = profile.deviceProfileBleAttributes || {}
      } else if (networkType === 'Zigbee') {
        networkAttributes = profile.deviceProfileZigbeeAttributes || {}
      }

      return {
        deviceProfileNumber: profile.deviceProfileNumber,
        deviceProfileName: profile.deviceProfileName,
        deviceProfileNetwork: profile.deviceProfileNetwork,
        region: networkAttributes?.region || profile.region,
        macVersion: networkAttributes?.macVersion || profile.macVersion,
        supportsOtaa: networkAttributes?.deviceSupportsOtaa ?? profile.supportsOtaa,
        supportsClassB: networkAttributes?.devicesupportsClassB ?? profile.supportsClassB,
      }
    })

  // Filter profiles
  const filteredProfiles = profiles.filter((profile) =>
    profile.deviceProfileName.toLowerCase().includes(deferredSearchQuery.toLowerCase())
  )

  // Pagination
  const totalPages = Math.ceil(filteredProfiles.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedProfiles = filteredProfiles.slice(startIndex, startIndex + itemsPerPage)



  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Layers className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Device Profiles</CardTitle>
                <CardDescription className="text-base">Manage network profiles for devices</CardDescription>
              </div>
            </div>
            <Button onClick={() => onAdd?.()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Profile
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search profiles..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          {/* Table */}
          <Table className="text-base">
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="font-semibold text-base w-[80px]">ID</TableHead>
                  <TableHead className="font-semibold text-base">Profile Name</TableHead>
                  <TableHead className="font-semibold text-base">Network</TableHead>
                  <TableHead className="font-semibold text-base">Region</TableHead>
                  <TableHead className="font-semibold text-base">MAC Version</TableHead>
                  <TableHead className="font-semibold text-base">OTAA</TableHead>
                  <TableHead className="font-semibold text-base">Class B</TableHead>
                  <TableHead className="w-[80px] text-right font-semibold text-base">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading profiles...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center text-destructive">
                      <div className="flex items-center justify-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Failed to load profiles
                      </div>
                    </TableCell>
                  </TableRow>
                ) : paginatedProfiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                      No profiles found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedProfiles.map((profile) => (
                    <TableRow key={profile.deviceProfileNumber}>
                      <TableCell className="text-base text-muted-foreground">{profile.deviceProfileNumber}</TableCell>
                      <TableCell className="font-medium text-base">
                        <Link
                          to="/sensory/device-profiles/$deviceProfileNumber"
                          params={{ deviceProfileNumber: String(profile.deviceProfileNumber) }}
                          className="text-primary hover:underline"
                        >
                          {profile.deviceProfileName}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="secondary" 
                          className={networkConfig[profile.deviceProfileNetwork]?.color || "bg-gray-100 text-gray-800"}
                        >
                          {profile.deviceProfileNetwork}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-base">
                        {profile.region || "-"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {profile.macVersion || "-"}
                      </TableCell>
                      <TableCell>
                        {profile.supportsOtaa ? (
                          <Badge variant="outline" className="bg-trilogy-blue/10 text-trilogy-blue border-trilogy-blue/20">Yes</Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">No</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {profile.supportsClassB ? (
                          <Badge variant="outline" className="bg-trilogy-blue/10 text-trilogy-blue border-trilogy-blue/20">Yes</Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">No</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                            <Link
                              to="/sensory/device-profiles/$deviceProfileNumber/edit"
                              params={{ deviceProfileNumber: String(profile.deviceProfileNumber) }}
                            >
                              <Edit className="h-4 w-4" />
                              <span className="sr-only">Edit {profile.deviceProfileName}</span>
                            </Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => handleDelete(profile)}
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete {profile.deviceProfileName}</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

          {/* Pagination */}
          {filteredProfiles.length > 0 && (
            <div className="border-t py-3 flex items-center justify-end space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="text-base text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Device Profile</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "<strong>{selectedProfile?.deviceProfileName}</strong>"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => { setIsDeleteDialogOpen(false); setSelectedProfile(null) }}
              disabled={deleteProfileMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteProfileMutation.isPending}
            >
              {deleteProfileMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
